import React from 'react';
import { MASTER_READING_PAGES } from './constants';
import FeedbackBar from '../../feedbackBar/FeedbackBar';
import withShipment from '../../../withShipment';
import { statusSelector } from '../../../redux/selectors/global';
import { clearStatus } from '../../../redux/actions/global';
import { userInfoSelector } from '../../../redux/selectors/auth';
import MasterReadingTable from './MasterReadingTable';
import {
  allSessionIdsSelector,
  differenceListSelector,
  employeeTicketsSelector,
  readingSelector,
  readingsSelector,
} from '../../../redux/selectors/settings';
import {
  clearReading,
  listMatchTickets,
  listReading,
  listSessionIds,
} from '../../../redux/actions/settings';
import MatchTicketsTable from './MatchTicketsTable';
import Utils from '../../Utils/Utils';
import ReadingSessionAuditLog from '../../audit/ReadingSessionAuditLog';
import {
  fromDisplayDate,
  getCurrentReadingWeek,
  getMondayFromTuesday,
  isMonday,
  toDisplayDate,
} from '../../Utils/readingWeek';

class MasterReading extends React.Component {
  constructor(props) {
    super(props);
    // When opened from the Weekly Report's Daily Analysis → Open Match Tickets,
    // deep-link straight into that day's Match Tickets view (nav state carries
    // the reading session id and the report state to return to).
    const nav = props.location?.state || {};
    // Same week filter as the Weekly Report: one Tue -> Mon reading week,
    // picked as Mon -> Sun (see Utils/readingWeek).
    const week = getCurrentReadingWeek();
    this.state = {
      currentPage: nav.openMatchTickets
        ? MASTER_READING_PAGES.MATCH_TICKETS
        : MASTER_READING_PAGES.TABLE,
      feedbackBarOpen: false,
      error: false,
      selectedDate: new Date(),
      // Stored Tue/Mon pair the pickers are editing
      fromDate: week.fromDate,
      toDate: week.toDate,
      // The week the loaded session IDs are for — moves only on Apply /
      // Reset, and drives the filter badge
      appliedFromDate: week.fromDate,
      appliedToDate: week.toDate,
      selectedSessionId: nav.sessionId || null,
    };
  }

  // Back out of the deep-linked Match Tickets view to the exact Weekly Report
  // the user came from (week/day/tab preserved via `restoreReport`).
  handleExitToReport = () => {
    const restore = this.props.location?.state?.restoreReport;
    if (this.props.history) {
      this.props.history.push(
        '/report',
        restore ? { restoreReport: restore } : undefined
      );
    }
  };

  componentDidMount() {
    this.props.clearReading();
    this.loadSessionIds(this.state.fromDate, this.state.toDate);
  }

  loadSessionIds = (fromDate, toDate) => {
    this.props.listSessionIds(
      Utils.formatTodayDate(fromDate),
      Utils.formatTodayDate(toDate),
      this.props.userInfo.selectedLocationId
    );
  };

  // The user picks the displayed Monday; state keeps the Tuesday it stands for
  handleFromDateChange = (displayDate) => {
    if (!displayDate || !isMonday(displayDate)) return;
    const fromDate = fromDisplayDate(displayDate);
    this.setState({ fromDate, toDate: getMondayFromTuesday(fromDate) });
  };

  // The displayed Sunday must be the one that closes fromDate's week
  handleToDateChange = (displayDate) => {
    const { fromDate } = this.state;
    if (!displayDate || !fromDate) return;
    const validSunday = toDisplayDate(getMondayFromTuesday(fromDate));
    if (displayDate.toDateString() !== validSunday.toDateString()) return;
    this.setState({ toDate: fromDisplayDate(displayDate) });
  };

  applyWeekFilter = () => {
    const { fromDate, toDate } = this.state;
    this.loadSessionIds(fromDate, toDate);
    this.clearSessionId();
    this.props.clearReading();
    this.setState({ appliedFromDate: fromDate, appliedToDate: toDate });
  };

  // Cancel drops an un-applied pick so the pickers match what is loaded
  cancelWeekFilter = () => {
    this.setState((prev) => ({
      fromDate: prev.appliedFromDate,
      toDate: prev.appliedToDate,
    }));
  };

  resetWeekFilter = () => {
    const week = getCurrentReadingWeek();
    this.setState(
      { fromDate: week.fromDate, toDate: week.toDate },
      this.applyWeekFilter
    );
  };

  clearSessionId = () => {
    this.setState({ selectedSessionId: null });
  };

  handleSessionChange = (event) => {
    this.setState({ selectedSessionId: event.target.value }, () => {
      this.refreshComponent(this.state.selectedSessionId);
    });
  };

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (
      this.props.status.statusText &&
      prevProps.status.statusText !== this.props.status.statusText
    ) {
      this.openFeedbackBar();
    }
  }

  refreshComponent = (sessionId) => {
    this.props.listReading(sessionId, this.props.userInfo.selectedLocationId);
  };

  setCurrentPage = (newPage) => {
    this.setState({
      currentPage: newPage,
    });
  };

  displayMatchTickets = () => {
    return (
      <MatchTicketsTable
        userInfo={this.props.userInfo}
        listMatchTickets={this.props.listMatchTickets}
        differenceList={this.props.differenceList}
        employeeTickets={this.props.employeeTickets}
        setCurrentPage={this.setCurrentPage}
        openFeedbackBar={this.openFeedbackBar}
        date={this.state.selectedDate}
        fromDate={this.state.fromDate}
        toDate={this.state.toDate}
        selectedSessionId={this.state.selectedSessionId}
        onExit={
          this.props.location?.state?.restoreReport
            ? this.handleExitToReport
            : undefined
        }
      />
    );
  };

  displayTable = () => {
    return (
      <MasterReadingTable
        userInfo={this.props.userInfo}
        refreshComponent={this.refreshComponent}
        readings={this.props.readings}
        setCurrentPage={this.setCurrentPage}
        openFeedbackBar={this.openFeedbackBar}
        fromDate={this.state.fromDate}
        toDate={this.state.toDate}
        appliedFromDate={this.state.appliedFromDate}
        appliedToDate={this.state.appliedToDate}
        handleFromDateChange={this.handleFromDateChange}
        handleToDateChange={this.handleToDateChange}
        applyWeekFilter={this.applyWeekFilter}
        cancelWeekFilter={this.cancelWeekFilter}
        resetWeekFilter={this.resetWeekFilter}
        locationId={this.props.userInfo.selectedLocationId}
        allSessionIds={this.props.allSessionIds}
        selectedSessionId={this.state.selectedSessionId}
        handleSessionChange={this.handleSessionChange}
        clearSessionId={this.clearSessionId}
        clearReading={this.props.clearReading}
      />
    );
  };

  displayAuditLog = () => {
    return (
      <ReadingSessionAuditLog
        embeddedSessionId={this.state.selectedSessionId}
        onBack={() => this.setCurrentPage(MASTER_READING_PAGES.TABLE)}
      />
    );
  };

  displayCurrentPage = () => {
    switch (this.state.currentPage) {
      case MASTER_READING_PAGES.TABLE:
        return this.displayTable();
      case MASTER_READING_PAGES.MATCH_TICKETS:
        return this.displayMatchTickets();
      case MASTER_READING_PAGES.AUDIT_LOG:
        return this.displayAuditLog();
      default:
        return <div>We encountered an error. Please refresh the page.</div>;
    }
  };

  openFeedbackBar = (status, message) => {
    this.setState({
      feedbackStatus: status,
      feedbackMessage: message,
      feedbackBarOpen: true,
    });
  };

  closeFeedbackBar = () => {
    this.setState(
      { feedbackBarOpen: false, feedbackStatus: '', feedbackMessage: '' },
      () => {
        this.props.clearStatus();
      }
    );
  };

  render() {
    return (
      <React.Fragment>
        <FeedbackBar
          open={this.state.feedbackBarOpen}
          handleClose={this.closeFeedbackBar}
          severity={this.state.feedbackStatus}
          message={this.state.feedbackMessage}
        />
        {this.displayCurrentPage()}
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state) => ({
  userInfo: userInfoSelector(state),
  status: statusSelector(state),
  reading: readingSelector(state),
  readings: readingsSelector(state),
  differenceList: differenceListSelector(state),
  employeeTickets: employeeTicketsSelector(state),
  allSessionIds: allSessionIdsSelector(state),
});

const actionCreators = {
  clearStatus,
  listReading,
  listMatchTickets,
  listSessionIds,
  clearReading,
};

export default withShipment(
  {
    actionCreators,
    mapStateToProps,
  },
  MasterReading
);
